import {
  IngredientProduct,
  ProductRecipe,
  RecipeUnit,
} from "./productRecipeApi";

export interface IngredientOption {
  value: number;
  label: string;
  product: IngredientProduct;
}
export interface DraftRow {
  key: number;
  product: IngredientProduct | null;
  quantity: string;
  unit: RecipeUnit;
  available: boolean;
}
export interface Draft {
  outputQuantity: string;
  outputUnit: RecipeUnit;
  notes: string;
  rows: DraftRow[];
}
export interface StoredDraft {
  version: string | null;
  draft: Draft;
}
export const unitsFor = (unit: number): RecipeUnit[] =>
  Number(unit) === 3 ? ["pieces"] : ["kg", "lbs"];
export const defaultUnit = (unit: number): RecipeUnit =>
  Number(unit) === 3 ? "pieces" : Number(unit) === 2 ? "lbs" : "kg";
export const optionFor = (product: IngredientProduct): IngredientOption => ({
  value: product.id,
  label: `${product.name} · ${product.plu}`,
  product,
});
export function fromRecipe(
  recipe: ProductRecipe | null,
  sellingUnit: number
): Draft {
  return {
    outputQuantity: recipe?.output_quantity ?? "1",
    outputUnit: recipe?.output_unit ?? defaultUnit(sellingUnit),
    notes: recipe?.notes ?? "",
    rows:
      recipe?.items.map((item, key) => ({
        key,
        product: item.ingredient,
        quantity: item.quantity,
        unit: item.unit,
        available: item.available,
      })) ?? [],
  };
}
export function validate(draft: Draft): Record<string, string[]> {
  const errors: Record<string, string[]> = {};
  const quantity = (value: string, unit: RecipeUnit, field: string) => {
    if (
      !/^\d+(\.\d{1,4})?$/.test(value) ||
      Number(value) <= 0 ||
      Number(value) > 9999999999.9999
    )
      errors[field] = [
        "Enter a positive quantity with up to four decimal places.",
      ];
    else if (unit === "pieces" && !Number.isInteger(Number(value)))
      errors[field] = ["Piece quantities must be whole numbers."];
  };
  quantity(draft.outputQuantity, draft.outputUnit, "output_quantity");
  if (!draft.rows.length) errors.items = ["Add at least one ingredient."];
  if (draft.notes.length > 10000)
    errors.notes = ["Use at most 10,000 characters."];
  const seen = new Set<number>();
  draft.rows.forEach((row, index) => {
    const field = `items.${index}`;
    if (!row.product || !row.available)
      errors[`${field}.ingredient_product_id`] = [
        "Choose an active ingredient product.",
      ];
    else if (seen.has(row.product.id))
      errors[`${field}.ingredient_product_id`] = [
        "This ingredient is already in the recipe.",
      ];
    if (row.product) seen.add(row.product.id);
    quantity(row.quantity, row.unit, `${field}.quantity`);
  });
  return errors;
}

export interface RecipeState {
  recipe: ProductRecipe | null;
  version: string | null;
  draft: Draft;
  baseline: string;
  editing: boolean;
  loaded: boolean;
  loading: boolean;
  saving: boolean;
  canManage: boolean;
  productAvailable: boolean;
  error: string;
  errors: Record<string, string[]>;
  conflict: boolean;
  notice: string;
}

export interface RecipeRuntime {
  productId: number;
  productSellingUnit: number;
  patch: (values: Partial<RecipeState>) => void;
  alive: { current: boolean };
  busy: { current: boolean };
  nextKey: { current: number };
  fail: (cause: unknown) => void;
}
